import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import type { Inspection } from '@lib/spproject/types';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Chip, InputAdornment, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAppSelector } from '@store/hooks';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { EmptyStateBox, InspectionItem, InspectionList, MainAccordion, MainAccordionDetails, MainAccordionSummary, OverlayContainer, SearchContainer, StyledTextField } from './NavOverlay.styled';

const NavOverlay: FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { t, currentLanguage } = useLocalization();

  const { project } = useAppSelector((state) => state.viewer);
  const navigate = useNavigate();

  const inspections = useMemo(() => { return project?.inspections ?? []; }, [project]);

  const filteredInspections = useMemo(() => {
    if (!searchQuery) {
      return inspections;
    }

    return inspections.filter((inspection) => inspection.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [inspections, searchQuery]);

  const openInspection = (projectId: string, inspectionId: string) => {
    const inspectionRoute = generatePath(ROUTE_PATHS.VIEWER_PROJECT_INSPECTION, { projectId, inspectionId });
    navigate(inspectionRoute);
  };

  const formatDate = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString(currentLanguage, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderInspectionItem = (inspection: Inspection.Info) => {
    return (
      <InspectionItem
        key={inspection.id}
        onClick={() => openInspection(project!.manifest.projectInfo.id, inspection.id)}
      >
        <ListItemIcon>
          <DescriptionIcon />
        </ListItemIcon>
        <ListItemText
          primary={<Typography
            noWrap
            variant="body1"
            fontWeight={500}
            sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {inspection.name}
          </Typography>}
          secondary={
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.2 }}>
              <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.6 }} />
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {formatDate(inspection.dateCreated)}
              </Typography>
            </Stack>
          }
          primaryTypographyProps={{
            variant: 'body1',
            fontWeight: 500,
          }}
          secondaryTypographyProps={{
            component: 'div',
          }}
        />
      </InspectionItem>
    );
  };

  return (
    <OverlayContainer>
      <MainAccordion defaultExpanded>
        <MainAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={inspections.length}
              size="small"
              color="primary"
              sx={{
                height: 24,
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: 12,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
              {t('Common_Inspections')}
            </Typography>

          </Stack>
        </MainAccordionSummary>
        <MainAccordionDetails>

          <SearchContainer>
            <StyledTextField
              placeholder={t('Project_Inspections_Search')}
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.7 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <Box
                      component="button"
                      onClick={() => setSearchQuery('')}
                      sx={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        p: 0.5,
                        color: 'text.secondary',
                        opacity: 0.7,
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    >
                      <SearchIcon fontSize="small" />
                    </Box>
                  </InputAdornment>
                ) : null,
              }}
            />
          </SearchContainer>

          {filteredInspections.length > 0 ? (
            <InspectionList>{filteredInspections.map((inspection) => renderInspectionItem(inspection))}</InspectionList>
          ) : (
            <EmptyStateBox>
              <SearchIcon sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" align="center" sx={{ fontWeight: 500 }}>
                No matching inspections
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, opacity: 0.7 }}>
                Try adjusting your search
              </Typography>
            </EmptyStateBox>
          )}
        </MainAccordionDetails>
      </MainAccordion>
    </OverlayContainer>
  );
};

export default NavOverlay;
